import { decisionSummaryMap } from "./constants.js";
import { RemoveYear, hasInErrorReason, formatEpisodeDetails, formatTreatmentDetails, getLatestChangeReason, getLatestChangeDate, } from "./helpers.js";
class LeavePeriodProtectionSearch {
    constructor() {
        this.format = (response) => {
            return {
                searchResultMetadata: this.extractSearchMetadata(response),
                LeaveSearch: this.processLeaves(response.leaves ?? []),
            };
        };
        this.extractSearchMetadata = (response) => ({
            startIndex: response.searchResultMetaData?.startIndex,
            totalResults: response.searchResultMetaData?.totalResults,
            itemsPerPage: response.searchResultMetaData?.itemsPerPage,
            timeTaken: response.searchResultMetaData?.resultMetadata?.timeTaken,
            links: response.searchResultMetaData?.resultMetaData?._links,
        });
        this.processLeaves = (leaves) => leaves
            .filter((leave) => {
            if (!leave.employee || !leave.leavePeriod)
                return false;
            return !hasInErrorReason(leave.leaveCloseReason, leave.leaveCloseReasonDesc);
        })
            .flatMap((leave) => this.processLeavePeriods(leave));
        this.processLeavePeriods = (leave) => leave.leavePeriod
            .filter((period) => !hasInErrorReason(period.periodStatusReason, period.leavePeriodStatusReasonDesc))
            .flatMap((period) => this.processPeriod(leave, period));
        this.processPeriod = (leave, period) => {
            const protections = this.getProtections(period);
            const certificationDetails = this.getCertificationDetails(period);
            const rtwRestrictions = this.getRtwRestrictions(period);
            const addressData = this.getAddressData(leave);
            const digitalAddresses = this.getDigitalAddresses(leave);
            const classificationList = this.getClassificationList(period);
            const uniqueProtections = new Set();
            return protections.flatMap((protection) => this.processProtection(leave, period, protection, certificationDetails, rtwRestrictions, addressData, digitalAddresses, classificationList, uniqueProtections));
        };
        this.processProtection = (leave, period, protection, certificationDetails, rtwRestrictions, addressData, digitalAddresses, classificationList, uniqueProtections) => {
            const uniqueKey = `${protection?.protectionId}-${protection?.protectionName}-${protection?.protectionStatusDesc}`;
            if (uniqueProtections.has(uniqueKey))
                return [];
            uniqueProtections.add(uniqueKey);
            return certificationDetails
                .flatMap((certificationDetail) => rtwRestrictions.map((rtwRestriction) => this.createEntry(leave, period, protection, certificationDetail, rtwRestriction, addressData, digitalAddresses, classificationList)))
                .filter((entry) => entry !== null);
        };
        this.getProtections = (period) => {
            return period.leavePeriodProtection?.length
                ? period.leavePeriodProtection
                : [this.getDefaultProtection()];
        };
        this.getDefaultProtection = () => ({
            protectionId: "",
            protectionName: "",
            protectionDisplayName: "",
            protectionType: "",
            srcProtectionType: "",
            protectionStatusReasonDesc: "",
            protectionStatusDesc: "",
            availabilityStatusDesc: "",
        });
        this.getCertificationDetails = (period) => {
            const leavePeriodDocuments = period.leavePeriodDocuments?.length
                ? period.leavePeriodDocuments
                : [{ documentId: "" }];
            const certificationDetails = leavePeriodDocuments.flatMap((document) => document.documentLatestRevision?.certificationDetails || [
                { certificationId: "" },
            ]);
            return certificationDetails.length > 0
                ? certificationDetails
                : [{ revisionId: "" }];
        };
        this.getRtwRestrictions = (period) => {
            const leavePeriodDocuments = period.leavePeriodDocuments?.length
                ? period.leavePeriodDocuments
                : [{ documentId: "" }];
            const documentAttributes = leavePeriodDocuments.flatMap((document) => document.documentLatestRevision?.documentAttributes || []);
            const rtwRestrictions = documentAttributes
                .filter((attribute) => attribute.attributeId === "1295")
                .map((attribute) => attribute.attributeValue)
                .filter((value) => value !== null && value !== undefined && value !== "");
            return rtwRestrictions.length > 0 ? rtwRestrictions : [null];
        };
        this.getAddressData = (leave) => {
            const postalAddresses = leave.employee?.postalAddresses || [
                { addressType: "" },
            ];
            const addressTypeToFilter = leave.leaveSourceSystem === "FNEOS" ? "Home" : "PA";
            return postalAddresses
                .filter((address) => address.addressType === addressTypeToFilter)
                .reduce((latest, current) => new Date(String(current.beginDate)) >
                new Date(String(latest.beginDate))
                ? current
                : latest, { beginDate: "01/01/1900" });
        };
        this.getDigitalAddresses = (leave) => {
            const digitalAddresses = leave.employee?.digitalAddresses ?? [];
            return {
                homeDigitalAddress: digitalAddresses.find((a) => a.digitalAddressTypeCd === "HE") ||
                    null,
                homeDigitalAddressFineos: digitalAddresses.find((a) => a.digitalAddressType?.toLowerCase() === "personal email") || null,
                workDigitalAddress: digitalAddresses.find((a) => a.digitalAddressTypeCd === "BE") ||
                    null,
                workDigitalAddressFineos: digitalAddresses.find((a) => a.digitalAddressType?.toLowerCase() === "work email") || null,
            };
        };
        this.getClassificationList = (period) => {
            const PeriodClassification = period.leavePeriodClassification || [];
            return PeriodClassification.length > 0
                ? PeriodClassification.map((classification) => classification?.classificiationTypeDesc || "")
                    .filter((desc) => desc !== "")
                    .join(", ")
                : "";
        };
        this.isClaimInError = (claim) => {
            const fields = [
                claim?.claimProcessingStage,
                claim?.customClaimStatusReason,
                claim?.claimStatusReason,
                claim?.claimStatusReasonDesc,
                claim?.claimStatusReasonCdDesc,
                claim?.customClaimStatusReasonCdDesc,
            ];
            return (hasInErrorReason(...fields) ||
                fields.some((field) => typeof field === "string" &&
                    field.toLowerCase().includes("markup in error")));
        };
        this.getRelatedClaimData = (leave) => {
            const relatedClaims = (leave.relatedClaimDetails ?? []).filter((claim) => !this.isClaimInError(claim));
            const filterAndJoin = (claims, property, transform = (v) => v) => {
                return claims
                    .map((claim) => transform(claim[property]))
                    .filter((value) => value !== null && value !== undefined && value !== "")
                    .join(", ");
            };
            return {
                hasVisibleRelatedClaims: relatedClaims.length > 0,
                relatedStdClaimIds: filterAndJoin(relatedClaims, "claimId"),
                relatedStdClaimStatuses: filterAndJoin(relatedClaims, "claimStatus"),
                relatedStdDivisionId: filterAndJoin(relatedClaims, "divisionId"),
                relatedStdDivisionName: filterAndJoin(relatedClaims, "divisionName"),
                disabilityBeginDt: filterAndJoin(relatedClaims, "disabilityBeginDt", RemoveYear),
                claimBenefitBeginDt: filterAndJoin(relatedClaims, "claimBenefitBeginDt"),
                claimApprovedThruDt: filterAndJoin(relatedClaims, "claimApprovedThruDt"),
                groupAgreementId: filterAndJoin(relatedClaims, "groupAgreementId"),
                policyName: filterAndJoin(relatedClaims, "policyName"),
                relatedStdClaimDuration: filterAndJoin(relatedClaims, "claimDuration"),
                relatedStdClaimStatusReason: filterAndJoin(relatedClaims, "customClaimStatusReason"),
                relatedStdMaxDurationDate: filterAndJoin(relatedClaims, "claimBenefitEndDt"),
            };
        };
        this.createEntry = (leave, period, protection, certificationDetail, rtwRestriction, addressData, digitalAddresses, classificationList) => {
            const relatedClaimData = this.getRelatedClaimData(leave);
            const consideredDisabilityInd = this.getConsideredDisabilityInd(leave);
            if (hasInErrorReason(leave.leaveCloseReason, leave.leaveCloseReasonDesc, period?.periodStatusReason, period?.leavePeriodStatusReasonDesc)) {
                return null;
            }
            const relatedClaimLinkedInd = relatedClaimData.hasVisibleRelatedClaims
                ? leave.relatedClaimLinkedInd
                : "N";
            if (hasInErrorReason(leave.leaveCloseReason, leave.leaveCloseReasonDesc, period?.periodStatusReason, period?.leavePeriodStatusReasonDesc)) {
                return null;
            }
            return {
                lastName: leave.employee?.lastName,
                jobTitle: leave.employee?.jobTitle,
                firstName: leave.employee?.firstName,
                taxIdentificationNumber: leave.employee?.taxIdentificationNumber,
                employeeId: leave.employee?.employeeId,
                partTimeRTWDt: leave.rtwType === "Part Time"
                    ? RemoveYear(leave.rtwDt || null)
                    : null,
                fullTimeRTWDt: leave.rtwType === "Full Time"
                    ? RemoveYear(leave.rtwDt || null)
                    : null,
                personDisplayName: leave.leaveSpecialist?.personDisplayName,
                coverageType: leave.coverageType,
                statusEffectiveDt: leave.leaveStatusEffectiveDt,
                lastDateWorked: leave.lastDateWorked,
                leaveId: leave.leaveId,
                leavePeriodNbr: period.leavePeriodNbr,
                leaveReason: leave.leaveReason,
                leaveRequestDt: leave.leaveRequestDt,
                leaveScheduleType: period.leaveScheduleType,
                leaveStatus: leave.leaveStatus,
                managerName: leave.employee?.managerName,
                managerEmail: leave.employee?.managerEmail,
                fullName: leave.employee?.name,
                workScheduleType: leave.employee?.workScheduleTypeDesc,
                periodEndDt: period.periodEndDt,
                periodStartDt: period.periodStartDt,
                periodStatus: period.periodStatus,
                periodStatusReason: period.periodStatusReason,
                receivedDt: leave.leaveReceivedDt,
                relatedClaimLinkedInd,
                reportGroupNumber: period.reportGroupNumber,
                estimatedDeliveryDt: period.estimatedDeliveryDt,
                reportGroupName: period.reportGroupName,
                employerReportGroupNumber: period.employerReportGroupNumber,
                reducedScheduleHours: period.reducedScheduleHours,
                rtwRestriction,
                rtwStatus: leave.rtwStatus,
                workState: leave.employee?.employeeWorkStateCd,
                historicalLeaveInd: leave.historicalLeaveInd,
                leaveStartDt: leave.leaveStartDt,
                leaveEndDt: leave.leaveEndDt,
                dateOfBirth: leave.employee?.dateOfBirth,
                age: leave.ageAtLeaveStart,
                leaveCloseReason: leave.leaveCloseReason,
                employerLocationName: leave.employee?.employerLocationName,
                city: addressData?.city || null,
                genderAtBirth: leave.employee?.genderAtBirth,
                hireDt: leave.employee?.hireDt || null,
                earningType: leave.employee?.earningType,
                firstHireDt: leave.employee?.firstHireDt,
                jurisdictionCode: addressData?.jurisdictionCode || null,
                addressLineOne: addressData?.addressLineOne ?? null,
                addressLineTwo: addressData?.addressLineTwo ?? null,
                rtwLastUpdateDt: leave.rtwLastUpdateDt,
                tenureDays: leave.employee?.tenureDays,
                postalCode: addressData?.postalCode || null,
                digitalAddressText: this.getDigitalAddressText(digitalAddresses, "home"),
                workDigitalAddressText: this.getDigitalAddressText(digitalAddresses, "work"),
                classificiationTypeDesc: classificationList,
                certBeginDate: certificationDetail.beginDt,
                certEndDate: certificationDetail.endDt,
                treatmentDetail: formatTreatmentDetails(certificationDetail.treatmentDetails, leave.leaveSourceSystem || ""),
                episodeDetail: formatEpisodeDetails(certificationDetail.episodeDetails, leave.leaveSourceSystem || ""),
                protectionType: protection?.protectionType,
                protectionName: protection?.protectionName,
                protectionStatusDesc: protection?.protectionStatusDesc,
                protectionId: protection?.protectionId,
                hoursAvailable: protection?.protectionHours?.hoursAvailable,
                hoursTaken: protection?.protectionHours?.hoursTaken,
                timeExhausted: protection?.protectionHours?.timeExhausted,
                timeBankMethod: protection?.protectionHours?.timeBankMethod,
                entitlementPeriod: protection?.entitlementPeriodMethodDesc || null,
                entitlementDescription: this.formatPlanEntitlement(protection?.protectionHours?.planEntitlementDesc, protection?.protectionHours?.planDurationDesc),
                actualDeliveryDt: period?.actualDeliveryDt,
                availabilityStatusDesc: decisionSummaryMap[protection?.availabilityStatusDesc],
                relatedStdClaimId: relatedClaimData.relatedStdClaimIds,
                relatedStdClaimStatus: relatedClaimData.relatedStdClaimStatuses,
                relatedStdDivisionId: relatedClaimData.relatedStdDivisionId,
                relatedStdDivisionName: relatedClaimData.relatedStdDivisionName,
                relatedDisabilityBeginDt: relatedClaimData.disabilityBeginDt,
                relatedStdPolicyNumber: relatedClaimData.groupAgreementId,
                relatedStdPolicyName: relatedClaimData.policyName,
                relatedStdClaimDuration: relatedClaimData.relatedStdClaimDuration,
                relatedStdClaimStatusReason: relatedClaimData.relatedStdClaimStatusReason,
                relatedStdMaxDurationDate: relatedClaimData.relatedStdMaxDurationDate,
                relatedStdClaimBenefitBeginDt: relatedClaimData.claimBenefitBeginDt,
                relatedStdClaimApprovedThruDt: relatedClaimData.claimApprovedThruDt,
                protectionStatusReasonDesc: protection?.protectionStatusReasonDesc,
                consideredDisabilityInd,
                pregnancyRelatedInd: leave?.pregnancyRelatedInd,
                fitForDutyReleaseDt: leave?.fitForDutyReleaseDt,
                leaveSubmitMethod: leave.leaveSubmissionSource,
                changeReason: getLatestChangeReason(leave),
                chgDate: getLatestChangeDate(leave),
            };
        };
        this.getConsideredDisabilityInd = (leave) => {
            const accommodation = (leave.accommodationDetails || [])[0];
            return accommodation?.consideredDisabilityInd || null;
        };
        this.getDigitalAddressText = (digitalAddresses, type) => {
            if (type === "home") {
                return (digitalAddresses.homeDigitalAddress?.digitalAddressText ||
                    digitalAddresses.homeDigitalAddressFineos?.digitalAddressText ||
                    null);
            }
            return (digitalAddresses.workDigitalAddress?.digitalAddressText ||
                digitalAddresses.workDigitalAddressFineos?.digitalAddressText ||
                null);
        };
        this.formatPlanEntitlement = (planEntitlementDesc, planDurationDesc) => {
            if (!planEntitlementDesc)
                return null;
            if (planDurationDesc) {
                return `${planEntitlementDesc} in ${planDurationDesc}`;
            }
            return planEntitlementDesc;
        };
    }
}
export default new LeavePeriodProtectionSearch();
